import axios from "axios";
import { MSGraphAuth } from "./GraphService";
import { MSGraphParamsEmail, ProjectParams } from "../config/GraphConfig";
import { urlFormat } from "../library/utils";
import {
  OffersAzureResponse,
  Setting,
  TaxonomyUADResponse,
} from "library/types/types";
import { SearchOffersArgs } from "state/queries/useGetOffers";
import { createAzureFilterParam } from "library/utils/azureSearchFilterParam";
import { toVisibleFilterColumns } from "library/utils/columnOptionsState";
import { routes } from "config/routes";

let authHeaders = {};
export type InvalidateOfferParams = {
  offerId: any;
};
const url = urlFormat(process.env.REACT_APP_BASE_URL || "");
const uadUrl = urlFormat(process.env.REACT_APP_BFF_HOST_UAD || "");

const axiosInstance = axios.create({
  baseURL: url,
});

async function execute(func: any, url: string, config: {}) {
  try {
    const response = await func(url, config);
    return {
      status: response?.status,
      statusText: response.statusText,
      data: response.data,
    };
  } catch (error: any) {
    if (error.response?.status === 403) {
      window.location.href = routes.unauthorizedPage.path;
    }
    // if (error.response) {
    //   const err = {
    //     status: error?.response?.status,
    //     statusText: error?.response?.statusText,
    //     error:
    //       error?.response?.data?.error || error?.response?.data?.data?.error,
    //     message:
    //       error?.response?.data?.message ||
    //       error?.response?.data?.data?.message ||
    //       "",
    //   };

    //   throw err;
    // }

    throw error;
  }
}

const getAppSettings = async (appID: number) => {
  return await axiosInstance.get<Setting[]>(`${uadUrl}/appsettings/${appID}`);
};

const getUADTaxonomy = async () => {
  return await axiosInstance.get<TaxonomyUADResponse>(`${uadUrl}/taxonomy`);
};

const addAppSetting = async (setting: Partial<Setting>) => {
  return await axiosInstance.post<Setting[]>(`${uadUrl}/addappsettings`, [
    setting,
  ]);
};

const updateAppSetting = async (setting: Setting) => {
  return await axiosInstance.patch<Setting[]>(`${uadUrl}/updateappsettings`, [
    setting,
  ]);
};

const invalidateOffer = async ({ offerId }: InvalidateOfferParams) => {
  let reqUrl: string = `${url}/offers/invalidate/${offerId}`;
  const res = await axiosInstance.patch<any>(reqUrl);
  return res;
};

const deleteAppSetting = async (
  userAppSettingId: Setting["userAppSettingId"]
) => {
  return await axiosInstance.delete<Setting[]>(`${uadUrl}/deleteappsettings`, {
    data: [userAppSettingId],
  });
};

const searchOffers = async ({
  skip,
  top,
  search,
  sort,
  cancelToken,
  columns,
  productFilter = "all",
  extraFilter,
  showMode,
}: SearchOffersArgs) => {
  let searchUrl = `${url}/offers`;
  // words with special characters (e.g. "-") are quoted on the backend
  searchUrl += `?searchCriteria=${encodeURIComponent(search ?? "")}*`;
  if (sort.key !== "" && sort.dir !== "") {
    searchUrl += `&$orderby=${sort.key} ${sort.dir}`;
  }
  searchUrl += `&form=json`;
  searchUrl += `&$count=true`;
  searchUrl += `&$top=${top}&$skip=${skip}`;
  // only admins send it; without it the backend lists the entries not marked as error
  if (showMode) {
    searchUrl += `&showMode=${showMode}`;
  }

  let filterStr = createAzureFilterParam(toVisibleFilterColumns(columns) as any);

  // Add product filter based on selection
  if (productFilter === "mtpl") {
    const mtplFilter = "OfferedInsurersMtpl/any()";
    filterStr =
      filterStr.length > 0 ? `${filterStr} and ${mtplFilter}` : mtplFilter;
  } else if (productFilter === "casco") {
    const cascoFilter = "OfferedInsurersCasco/any()";
    filterStr =
      filterStr.length > 0 ? `${filterStr} and ${cascoFilter}` : cascoFilter;
  }

  if (extraFilter) {
    filterStr =
      filterStr.length > 0 ? `${filterStr} and (${extraFilter})` : extraFilter;
  }

  if (filterStr.length > 0) {
    searchUrl += `&$filter=${filterStr}`;
  }

  const res = await axiosInstance.get<OffersAzureResponse>(searchUrl, {
    cancelToken,
  });
  return res;
};

const api = {
  setAuthHeaders: (headers: {}) => {
    authHeaders = {
      ...authHeaders,
      ...headers,
    };
  },
  get: async (url: string, config = {}) =>
    execute(axiosInstance.get, url, config),
  post: async (url: string, config = {}) =>
    execute(axiosInstance.post, url, config),
  put: async (url: string, config = {}) =>
    execute(axiosInstance.put, url, config),
  patch: async (url: string, config = {}) =>
    execute(axiosInstance.patch, url, config),
  delete: async (url: string, config = {}) =>
    execute(axiosInstance.delete, url, config),
  getAppSettings: getAppSettings,
  getUADTaxonomy: getUADTaxonomy,
  addAppSetting: addAppSetting,
  deleteAppSetting: deleteAppSetting,
  updateAppSetting: updateAppSetting,
  invalidateOffer: invalidateOffer,
  searchOffers: searchOffers,
};

export default api;

axiosInstance.interceptors.request.use(async (config) => {
  let authResponse = await MSGraphAuth.getAccessToken(ProjectParams);
  if (!authResponse) return config;
  if (config.url?.indexOf("photo/$value") !== -1) {
    return config;
  }
  config!.headers!.Authorization = `Bearer ${authResponse.accessToken}`;


  try {
    let emailAuthResponse = await MSGraphAuth.getAccessToken(
      MSGraphParamsEmail
    );
    if (emailAuthResponse && emailAuthResponse.accessToken) {
      config!.headers!["X-GraphToken"] = emailAuthResponse.accessToken;
    }
  } catch (error) {
    // Continue without email token if it fails
    console.warn("Failed to get email token:", error);
  }
 
  return config;
});
